/**
 * Knowledge → Overview (9 Oct): counts by status and area, what is due for
 * review, and the latest changes. Read-only for every IT user.
 */

import { Badge, Button, Statistic, Typography } from "@norican/react";
import { Link } from "react-router";

import {
  AppBreadcrumbs,
  ErrorState,
  LoadingRows,
  PageContainer,
  RelativeTime,
  SectionCard,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";

import { useKnowledgeOverview } from "./api";
import { KnowledgeGate, ReviewBadge } from "./shared";

export function KnowledgeOverviewPage() {
  const { t } = useT();
  useDocumentTitle(t("knowledgeAdmin.overview.title"));
  return (
    <KnowledgeGate title={t("knowledgeAdmin.overview.title")}>
      <OverviewBody />
    </KnowledgeGate>
  );
}

function OverviewBody() {
  const { t } = useT();
  const overview = useKnowledgeOverview();
  const data = overview.data;
  return (
    <PageContainer
      title={t("knowledgeAdmin.overview.title")}
      subtitle={t("knowledgeAdmin.overview.subtitle")}
      breadcrumbs={<AppBreadcrumbs items={[{ label: t("knowledgeAdmin.overview.title") }]} />}
      width="wide"
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" render={<Link to="/knowledge/articles" />}>
            {t("knowledgeAdmin.nav.articles")}
          </Button>
          {data?.can_edit ? (
            <Button render={<Link to="/knowledge/bulk-add" />}>
              {t("knowledgeAdmin.nav.bulkAdd")}
            </Button>
          ) : null}
        </div>
      }
    >
      {overview.isLoading ? (
        <LoadingRows rows={5} />
      ) : overview.error != null || data === undefined ? (
        <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
      ) : (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <Statistic label={t("knowledgeAdmin.overview.total")} value={String(data.total)} />
            <Statistic
              label={t("knowledgeAdmin.status.published")}
              value={String(data.status_counts.published ?? 0)}
              tone="success"
            />
            <Statistic
              label={t("knowledgeAdmin.status.draft")}
              value={String(data.status_counts.draft ?? 0)}
            />
            <Statistic
              label={t("knowledgeAdmin.status.retired")}
              value={String(data.status_counts.retired ?? 0)}
            />
            <Statistic
              label={t("knowledgeAdmin.overview.notIndexed")}
              value={String(data.not_indexed)}
              tone={data.not_indexed > 0 ? "danger" : "neutral"}
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <SectionCard
              title={t("knowledgeAdmin.overview.byArea")}
              description={t("knowledgeAdmin.overview.withoutArea", { count: data.without_area })}
            >
              <ul className="flex list-none flex-col gap-1 p-0">
                {data.by_area.map((area) => (
                  <li key={area.id} className="flex items-center justify-between gap-2">
                    <Link
                      to={`/knowledge/articles?area=${encodeURIComponent(area.id)}`}
                      className="text-accent-fg-subtle text-sm hover:underline"
                    >
                      {area.name}
                    </Link>
                    <span className="text-fg-muted text-sm">
                      {t("knowledgeAdmin.overview.areaCount", {
                        published: area.published,
                        total: area.total,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            </SectionCard>

            <SectionCard title={t("knowledgeAdmin.overview.reviewDue")}>
              {data.review_due.length === 0 ? (
                <Typography variant="body-sm" tone="muted">
                  {t("knowledgeAdmin.overview.nothingDue")}
                </Typography>
              ) : (
                <ul className="flex list-none flex-col gap-2 p-0">
                  {data.review_due.map((article) => (
                    <li key={article.id} className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/knowledge/articles/${article.id}`}
                        className="text-accent-fg-subtle text-sm hover:underline"
                      >
                        {article.title}
                      </Link>
                      <ReviewBadge state={article.review_state} />
                      <span className="text-fg-muted text-xs">{article.review_due}</span>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>

          <SectionCard title={t("knowledgeAdmin.overview.recent")}>
            {data.recent.length === 0 ? (
              <Typography variant="body-sm" tone="muted">
                {t("knowledgeAdmin.overview.noneYet")}
              </Typography>
            ) : (
              <ul className="flex list-none flex-col gap-2 p-0">
                {data.recent.map((change, index) => (
                  <li
                    key={`${change.article_id}-${change.version}-${index}`}
                    className="flex flex-wrap items-center gap-2 text-sm"
                  >
                    <Badge tone="neutral" variant="outline" size="sm">
                      {t(`knowledgeAdmin.action.${change.action as "edit"}`, {
                        defaultValue: change.action,
                      })}
                    </Badge>
                    <Link
                      to={`/knowledge/articles/${change.article_id}`}
                      className="text-accent-fg-subtle hover:underline"
                    >
                      {change.title}
                    </Link>
                    <span className="text-fg-muted">
                      {change.created_by} · <RelativeTime value={change.created_at} />
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>
      )}
    </PageContainer>
  );
}
